import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, MessageSquare, Mail, Calendar, ChevronRight } from "lucide-react";
import { useLocation, Link } from "react-router-dom";

const HIDE = ["/admin", "/login"];
const PHONE_TEL = "+18338237765";
const PHONE_DISPLAY = "(833) 8BESPOKE";
const SMS_TEL = "+15182472467";
const EMAIL = "hello@bespokeexteriorsolutions.com";

const ACTIONS = [
  { id: "call",  icon: Phone,          label: "Call",     sub: PHONE_DISPLAY, href: `tel:${PHONE_TEL}`, kind: "link" },
  { id: "sms",   icon: MessageSquare,  label: "Text",     sub: "Text an operator", href: `sms:${SMS_TEL}?body=Interested%20in%20a%20quote`, kind: "link" },
  { id: "email", icon: Mail,           label: "Email",    sub: "Response ≤ 24 h", href: `mailto:${EMAIL}?subject=Quote%20Request`, kind: "link" },
  { id: "book",  icon: Calendar,       label: "Schedule", sub: "Consultation",   to: "/audit", kind: "route" },
];

/** Always-visible floating action rail on the right side of every page.
 * Expands on hover to reveal labels; single tap = single action. */
export default function ActionRail() {
  const location = useLocation();
  const [hovered, setHovered] = useState(null);
  const [visible, setVisible] = useState(false);
  const hidden = HIDE.some((r) => location.pathname.startsWith(r));

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 1200);
    return () => clearTimeout(t);
  }, []);

  if (hidden) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 40 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="fixed right-3 md:right-5 top-1/2 -translate-y-1/2 z-40 hidden sm:flex flex-col gap-2"
          onMouseLeave={() => setHovered(null)}
          data-testid="action-rail"
        >
          {ACTIONS.map((a, i) => {
            const Icon = a.icon;
            const isHovered = hovered === a.id;
            const inner = (
              <motion.div
                className="relative flex items-center justify-end"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.08 * i, ease: [0.16, 1, 0.3, 1] }}
                onMouseEnter={() => setHovered(a.id)}
              >
                <AnimatePresence>
                  {isHovered && (
                    <motion.div
                      key="label"
                      initial={{ opacity: 0, x: 20, scale: 0.92 }}
                      animate={{ opacity: 1, x: 0, scale: 1 }}
                      exit={{ opacity: 0, x: 20, scale: 0.92 }}
                      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                      className="absolute right-full mr-3 whitespace-nowrap pr-1"
                    >
                      <div
                        className="px-4 py-2.5"
                        style={{
                          background: "rgba(11,11,13,0.9)",
                          border: "1px solid var(--line-gold)",
                          borderRadius: "var(--r-full)",
                          backdropFilter: "blur(14px)",
                          boxShadow: "0 12px 32px -8px rgba(0,0,0,0.75), 0 0 24px rgba(198,168,124,0.14)",
                        }}
                      >
                        <div className="font-mono text-[10px] uppercase tracking-[0.32em]" style={{ color: "var(--gold)" }}>
                          {a.label}
                        </div>
                        <div className="font-mono text-[9px] tracking-[0.2em] mt-0.5" style={{ color: "var(--text-3)" }}>
                          {a.sub}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <motion.div
                  whileHover={{ scale: 1.08 }}
                  whileTap={{ scale: 0.94 }}
                  transition={{ type: "spring", stiffness: 380, damping: 22 }}
                  className="flex items-center justify-center w-12 h-12 md:w-14 md:h-14 transition-all duration-500"
                  style={{
                    background: isHovered ? "var(--gold)" : "rgba(20,20,23,0.78)",
                    color: isHovered ? "#0b0b0d" : "var(--gold)",
                    border: `1px solid ${isHovered ? "var(--gold)" : "var(--line-gold)"}`,
                    borderRadius: "var(--r-full)",
                    backdropFilter: "blur(16px) saturate(140%)",
                    boxShadow: isHovered
                      ? "0 16px 40px -6px rgba(0,0,0,0.85), 0 0 32px rgba(198,168,124,0.42)"
                      : "0 10px 28px -8px rgba(0,0,0,0.7), 0 0 20px rgba(198,168,124,0.12)",
                  }}
                >
                  <Icon size={16} strokeWidth={1.6} />
                </motion.div>
              </motion.div>
            );
            const testid = `rail-${a.id}`;
            return a.kind === "link" ? (
              <a key={a.id} href={a.href} data-testid={testid} aria-label={a.label} data-cursor="hover">{inner}</a>
            ) : (
              <Link key={a.id} to={a.to} data-testid={testid} aria-label={a.label} data-cursor="hover">{inner}</Link>
            );
          })}

          {/* Rail connector line */}
          <div className="absolute right-6 md:right-7 top-full mt-2 w-px h-8" style={{ background: "linear-gradient(to bottom, var(--line-gold), transparent)" }} />
          <div className="absolute right-6 md:right-7 bottom-full mb-2 w-px h-8" style={{ background: "linear-gradient(to top, var(--line-gold), transparent)" }} />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
